import { Inject, Injectable, InjectionToken } from '@angular/core';
import { PaginatedStateEffects } from '@shared/ui';
import {
  type IAnyMethodTypesForAssetTypeQuery,
  type ICountAssetTypesQuery,
  type ICountCaseTypesByAssetTypeQuery,
  type ICountMethodTypesByAssetTypeQuery,
  type IDeleteAssetTypeCommand,
  type IListAssetTypesQuery,
  type IRetrieveAssetTypeQuery,
  SaveAssetType,
  SaveAssetTypeDetails,
  SaveAssetTypeHandler,
} from '@protocols/use-cases';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { EMPTY, from, merge, Observable, Subject } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import { AssetTypeData, AssetTypeEditorData, AssetTypeState, AssetTypeStateModel } from '../state/asset-type.state';

export const COUNT_ASSET_TYPES_QUERY = new InjectionToken<ICountAssetTypesQuery>('COUNT_ASSET_TYPES_QUERY');
export const LIST_ASSET_TYPES_QUERY = new InjectionToken<IListAssetTypesQuery>('LIST_ASSET_TYPES_QUERY');
export const DELETE_ASSET_TYPE_COMMAND = new InjectionToken<IDeleteAssetTypeCommand>('DELETE_ASSET_TYPE_COMMAND');
export const RETRIEVE_ASSET_TYPE_QUERY = new InjectionToken<IRetrieveAssetTypeQuery>('RETRIEVE_ASSET_TYPE_QUERY');
export const ANY_METHOD_TYPES_FOR_ASSET_TYPE_QUERY = new InjectionToken<IAnyMethodTypesForAssetTypeQuery>(
  'ANY_METHOD_TYPES_FOR_ASSET_TYPE_QUERY'
);
export const COUNT_METHOD_TYPES_BY_ASSET_TYPE_QUERY = new InjectionToken<ICountMethodTypesByAssetTypeQuery>(
  'COUNT_METHOD_TYPES_BY_ASSET_TYPE_QUERY'
);
export const COUNT_CASE_TYPES_BY_ASSET_TYPE_QUERY = new InjectionToken<ICountCaseTypesByAssetTypeQuery>(
  'COUNT_CASE_TYPES_BY_ASSET_TYPE_QUERY'
);
export const SAVE_ASSET_TYPE_HANDLER = new InjectionToken<SaveAssetTypeHandler>('SAVE_ASSET_TYPE_HANDLER');

@Injectable()
export class AssetTypeEffects extends PaginatedStateEffects<AssetTypeData, AssetTypeStateModel> {
  public readonly saveCompleted$ = new Subject<AssetTypeEditorData>();

  constructor(
    public readonly assetTypeState: AssetTypeState,
    @Inject(COUNT_ASSET_TYPES_QUERY) private readonly countAssetTypesQuery: ICountAssetTypesQuery,
    @Inject(LIST_ASSET_TYPES_QUERY) private readonly listAssetTypesQuery: IListAssetTypesQuery,
    @Inject(DELETE_ASSET_TYPE_COMMAND) private readonly deleteAssetTypeCommand: IDeleteAssetTypeCommand,
    @Inject(RETRIEVE_ASSET_TYPE_QUERY) private readonly retrieveAssetTypeQuery: IRetrieveAssetTypeQuery,
    @Inject(ANY_METHOD_TYPES_FOR_ASSET_TYPE_QUERY)
    private readonly anyMethodTypesForAssetTypeQuery: IAnyMethodTypesForAssetTypeQuery,
    @Inject(COUNT_METHOD_TYPES_BY_ASSET_TYPE_QUERY)
    private readonly countMethodTypesByAssetTypeQuery: ICountMethodTypesByAssetTypeQuery,
    @Inject(COUNT_CASE_TYPES_BY_ASSET_TYPE_QUERY)
    private readonly countCaseTypesByAssetTypeQuery: ICountCaseTypesByAssetTypeQuery,
    @Inject(SAVE_ASSET_TYPE_HANDLER) private readonly saveAssetTypeHandler: SaveAssetTypeHandler
  ) {
    super(assetTypeState);
  }

  // ============================================================================
  // Re-exposed Selectors from State
  // ============================================================================

  public readonly selectedAssetType$ = this.assetTypeState.selectedAssetType$;
  public readonly saving$ = this.assetTypeState.saving$;
  public readonly saved$ = this.assetTypeState.saved$;
  public readonly isNewAssetType$ = this.assetTypeState.isNewAssetType$;

  // Backwards-compat aliases
  public readonly assetTypes$ = this.assetTypeState.assetTypes$;
  public readonly nextAssetTypeToken$ = this.assetTypeState.nextAssetTypeToken$;
  public readonly previousAssetTypeTokens$ = this.assetTypeState.previousAssetTypeTokens$;
  public readonly assetTypesLoaded$ = this.assetTypeState.assetTypesLoaded$;
  public readonly noAssetTypes$ = this.assetTypeState.noAssetTypes$;

  // ============================================================================
  // Re-exposed Updaters from State
  // ============================================================================

  public readonly initializeNewAssetType = this.assetTypeState.initializeNewAssetType;
  public readonly resetEditor = this.assetTypeState.resetEditor;
  public readonly setSearchTerm = this.assetTypeState.setSearchTerm;
  public readonly changePageSize = this.assetTypeState.changePageSize;

  // ============================================================================
  // Backwards-compat effect aliases
  // ============================================================================

  public readonly deleteAssetType = this.deleteItem;
  public readonly loadMoreAssetTypes = this.loadMoreItems;
  public readonly initializeAssetTypes = this.initializeItems;
  public readonly countAssetTypes = this.countItems;

  // ============================================================================
  // Custom Effects
  // ============================================================================

  /**
   * Load an existing asset type for editing
   */
  public readonly loadAssetTypeForEdit = this.effect((assetTypeId$: Observable<string>) =>
    assetTypeId$.pipe(
      tap(() => this.assetTypeState.setLoading(true)),
      switchMap((id) =>
        from(this.retrieveAssetTypeQuery.execute(id)).pipe(
          tap((assetType) => {
            this.assetTypeState.setSelectedAssetType({
              id: assetType.id,
              name: assetType.name,
              description: assetType.description,
              type: assetType.type,
              version: assetType.version,
              deviceTypeIds: [...assetType.deviceTypeIds],
            });
          }),
          catchError((error) => {
            this.assetTypeState.setEditorError(error.message);
            return EMPTY;
          })
        )
      )
    )
  );

  public readonly assignDeviceType = this.effect(
    (action$: Observable<{ deviceTypeId: string; assetTypeIds: string[] }>) =>
      action$.pipe(
        tap(({ deviceTypeId, assetTypeIds }) => {
          this.assetTypeState.updateItemById({ id: deviceTypeId, changes: { deviceTypeIds: assetTypeIds } });
        })
      )
  );

  /**
   * Save the current asset type (create or update)
   */
  public readonly saveAssetType = this.effect(
    (
      trigger$: Observable<{
        name: string;
        description: string;
        type: 'image' | 'video' | 'text' | 'audio';
        version: string;
        deviceTypeIds: string[];
      }>
    ) =>
      trigger$.pipe(
        tap(() => this.assetTypeState.setSaving(true)),
        concatLatestFrom(() => this.assetTypeState.selectedAssetType$),
        switchMap(([{ name, description, type, version, deviceTypeIds }, selectedAssetType]) => {
          const input: SaveAssetTypeDetails = {
            id: selectedAssetType?.id,
            name,
            description: description ?? selectedAssetType?.description,
            type: type ?? selectedAssetType?.type,
            version: version ?? selectedAssetType?.version,
            deviceTypeIds: deviceTypeIds ?? selectedAssetType?.deviceTypeIds,
          };
          return from(this.saveAssetTypeHandler.execute(new SaveAssetType(input))).pipe(
            tap((id) => {
              const editorData: AssetTypeEditorData = {
                id,
                name,
                description,
                type,
                version,
                deviceTypeIds,
              };
              this.assetTypeState.setSaved(editorData);
              this.saveCompleted$.next(editorData);
            }),
            catchError((error) => {
              this.assetTypeState.setEditorError(error.message);
              return EMPTY;
            })
          );
        })
      )
  );

  // ============================================================================
  // Public Methods
  // ============================================================================

  public canBeDeleted(item: AssetTypeData): Observable<boolean> {
    return from(this.anyMethodTypesForAssetTypeQuery.execute(item.id)).pipe(map((any) => !any));
  }

  // ============================================================================
  // Abstract Implementations
  // ============================================================================

  protected async countRepository(searchTerm?: string): Promise<number> {
    return this.countAssetTypesQuery.execute(searchTerm);
  }

  protected async deleteRepositoryItem(itemId: string): Promise<boolean> {
    try {
      await this.deleteAssetTypeCommand.execute(itemId);
      return true;
    } catch {
      return false;
    }
  }

  protected listFlow(
    limit: number,
    lastItem: AssetTypeData | undefined,
    searchTerm: string | undefined
  ): Observable<AssetTypeData[]> {
    return from(
      this.listAssetTypesQuery.execute({
        searchTerm,
        limit,
        lastItemName: lastItem?.name,
      })
    ).pipe(
      map((items) =>
        items.map((item) => ({
          id: item.id,
          name: item.name,
          type: item.type,
          supportedDevices: item.supportedDevices,
          deviceTypeIds: item.deviceTypeIds,
          usedByCaseTypes: undefined,
          usedByMethodTypes: undefined,
        }))
      )
    );
  }

  protected processDeferredAttributes(itemId: string): Observable<Partial<AssetTypeData>> {
    const caseTypes$ = this.countAttribute(this.countCaseTypesByAssetTypeQuery.execute(itemId), itemId).pipe(
      map((usedByCaseTypes) => ({ usedByCaseTypes }))
    );
    const methodTypes$ = this.countAttribute(this.countMethodTypesByAssetTypeQuery.execute(itemId), itemId).pipe(
      map((usedByMethodTypes) => ({ usedByMethodTypes }))
    );
    return merge(caseTypes$, methodTypes$);
  }
}
